import { useEffect, useState, useMemo } from 'react'
import useSafeInfo from '@/hooks/useSafeInfo'
import { checkHederaSufficientApprovals } from '@/services/hedera/tx-dispatcher'
import type { HederaApprovalStatus } from '@/services/hedera/types'
import { useCurrentChain } from '@/hooks/useChains'
import { createWeb3ReadOnly } from '@/hooks/wallets/web3'
import type { SafeTransaction } from '@safe-global/safe-core-sdk-types'

/**
 * Hook to check approval status of a Hedera Safe transaction
 * Uses a read-only provider created from the chain info for contract queries
 */
export const useHederaApprovalStatus = (
  safeTxHash: string | undefined,
  safeTx: SafeTransaction | undefined,
  enabled: boolean = true,
): {
  approvalStatus: HederaApprovalStatus | undefined
  loading: boolean
  error: Error | undefined
  refetch: () => Promise<void>
} => {
  const { safe } = useSafeInfo()
  const chain = useCurrentChain()
  const [approvalStatus, setApprovalStatus] = useState<HederaApprovalStatus>()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error>()

  // Create a read-only provider from the chain info
  const readOnlyProvider = useMemo(() => {
    if (!chain) return undefined
    return createWeb3ReadOnly(chain)
  }, [chain])

  const fetchApprovalStatus = async () => {
    if (!safeTxHash || !readOnlyProvider || !enabled || !safeTx) {
      return
    }

    setLoading(true)
    setError(undefined)

    try {
      const status = await checkHederaSufficientApprovals(
        readOnlyProvider,
        safe.address.value,
        safeTxHash,
        safeTx,
        safe.owners.map((o) => o.value),
      )
      setApprovalStatus(status)
    } catch (err) {
      setError(err as Error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchApprovalStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeTxHash, readOnlyProvider, safe.address.value, safe.owners, enabled])

  return {
    approvalStatus,
    loading,
    error,
    refetch: fetchApprovalStatus,
  }
}
